/**
 * NotificationPage
 * FindMyLawyer – Lawyer Portal
 *
 * Full notification center with:
 * - Filter tabs: All / Unread / Bookings / Messages / Meetings / Payments / Verification / System
 * - Cursor-based infinite scroll
 * - Date-grouped sections (Today / Yesterday / Earlier)
 * - Bulk actions: Mark All Read / Archive All Read
 * - Empty state
 * - Dev seed button (visible only in development)
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Archive, Bell, Bot, Calendar, Check, CheckCircle,
  FileText, Filter, Headphones, IndianRupee, Info,
  Loader2, MessageCircle, RefreshCw, ShieldCheck, Sparkles,
  Star, Trash2, Video, Wallet, XCircle,
} from "lucide-react";
import { NotificationCard } from "../components/NotificationCard";
import { FILTER_TABS, getNotificationConfig, PRIORITY_COLORS } from "../utils/notificationConfig";
import { useNotifications } from "../context/NotificationContext";
import { seedNotifications } from "../services/notificationService";

// ── Date group helper ─────────────────────────────────────────────────────────
function getDateGroup(dateStr) {
  const d   = new Date(dateStr);
  const now = new Date();
  const todayStart     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart - 86400000);

  if (d >= todayStart)     return "Today";
  if (d >= yesterdayStart) return "Yesterday";
  return "Earlier";
}

function buildGroups(notifications) {
  const order  = ["Today", "Yesterday", "Earlier"];
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  for (const n of notifications) {
    const g = getDateGroup(n.created_at);
    groups[g].push(n);
  }
  return order.map((label) => ({ label, items: groups[label] })).filter((g) => g.items.length > 0);
}

// ── Notification stats bar ────────────────────────────────────────────────────
function StatsBar({ notifications, unreadCount }) {
  const total    = notifications.length;
  const archived = 0;
  return (
    <div style={{
      display:        "flex",
      gap:            "12px",
      marginBottom:   "16px",
      flexWrap:       "wrap",
    }}>
      {[
        { label: "Total",  value: total,       color: "#3b82f6" },
        { label: "Unread", value: unreadCount,  color: "#ef4444" },
      ].map((s) => (
        <div key={s.label} style={{
          background:   "#ffffff",
          border:       "1px solid #e2e8f0",
          borderRadius: "10px",
          padding:      "10px 16px",
          display:      "flex",
          alignItems:   "center",
          gap:          "8px",
        }}>
          <span style={{ fontSize: "20px", fontWeight: 800, color: s.color }}>{s.value}</span>
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export const NotificationPage = ({ onNavigate }) => {
  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchNotifications,
    loadMore,
    markAllRead,
    archiveNotification,
  } = useNotifications();

  const [activeTab, setActiveTab] = useState("ALL");
  const [seeding,   setSeeding]   = useState(false);
  const loaderRef = useRef(null);

  // ── Apply filter on tab change ────────────────────────────────────────────
  const applyFilter = useCallback((tabId) => {
    setActiveTab(tabId);
    const tab = FILTER_TABS.find((t) => t.id === tabId);
    if (!tab) return;

    if (tabId === "UNREAD") {
      fetchNotifications({ status: "UNREAD" });
    } else if (tabId === "ALL") {
      fetchNotifications({});
    } else {
      fetchNotifications({ type: tab.type });
    }
  }, [fetchNotifications]);

  // ── Infinite scroll via IntersectionObserver ──────────────────────────────
  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) loadMore(); },
      { threshold: 0.5 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  // ── Archive all read ──────────────────────────────────────────────────────
  const handleArchiveAllRead = async () => {
    const readIds = notifications
      .filter((n) => n.status === "READ")
      .map((n) => n.id);
    await Promise.all(readIds.map((id) => archiveNotification(id)));
  };

  // ── Dev seed ──────────────────────────────────────────────────────────────
  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedNotifications();
      await fetchNotifications();
    } catch (e) {
      console.error("Seed failed", e);
    } finally {
      setSeeding(false);
    }
  };

  const groups      = buildGroups(notifications);
  const hasReadItems = notifications.some((n) => n.status === "READ");
  const isDev        = process.env.NODE_ENV !== "production";

  return (
    <div style={{ maxWidth: "860px", margin: "0 auto", padding: "4px 0 40px" }}>

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        marginBottom:   "20px",
        flexWrap:       "wrap",
        gap:            "12px",
      }}>
        <div>
          <h1 style={{
            fontSize:   "22px",
            fontWeight: 800,
            color:      "#0f172a",
            display:    "flex",
            alignItems: "center",
            gap:        "10px",
          }}>
            <span style={{
              width:          "36px",
              height:         "36px",
              borderRadius:   "10px",
              background:     "linear-gradient(135deg, #3b82f6, #1d4ed8)",
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              color:          "#fff",
            }}>
              <Bell size={18} />
            </span>
            Notifications
            {unreadCount > 0 && (
              <span style={{
                background:   "#ef4444",
                color:        "#fff",
                borderRadius: "8px",
                fontSize:     "12px",
                fontWeight:   700,
                padding:      "2px 8px",
              }}>
                {unreadCount}
              </span>
            )}
          </h1>
          <p style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
            All your important activity in one place.
          </p>
        </div>

        {/* Bulk actions */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {isDev && (
            <button
              onClick={handleSeed}
              disabled={seeding}
              style={{
                padding:      "7px 12px",
                borderRadius: "9px",
                border:       "1px solid #e2e8f0",
                background:   "#f8fafc",
                color:        "#64748b",
                fontSize:     "12px",
                fontWeight:   600,
                cursor:       "pointer",
                display:      "flex",
                alignItems:   "center",
                gap:          "5px",
              }}
            >
              {seeding ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Seed Test Data
            </button>
          )}

          {hasReadItems && (
            <button
              onClick={handleArchiveAllRead}
              style={{
                padding:      "7px 12px",
                borderRadius: "9px",
                border:       "1px solid #e2e8f0",
                background:   "#ffffff",
                color:        "#64748b",
                fontSize:     "12px",
                fontWeight:   600,
                cursor:       "pointer",
                display:      "flex",
                alignItems:   "center",
                gap:          "5px",
              }}
            >
              <Archive size={13} /> Archive Read
            </button>
          )}

          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              style={{
                padding:      "7px 14px",
                borderRadius: "9px",
                border:       "none",
                background:   "#0f172a",
                color:        "#ffffff",
                fontSize:     "12px",
                fontWeight:   600,
                cursor:       "pointer",
                display:      "flex",
                alignItems:   "center",
                gap:          "5px",
                transition:   "opacity 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              id="mark-all-read-btn"
            >
              <Check size={13} /> Mark All Read
            </button>
          )}

          <button
            onClick={() => fetchNotifications({})}
            style={{
              minWidth:       "40px",
              minHeight:      "40px",
              width:          "40px",
              height:         "40px",
              borderRadius:   "9px",
              border:         "1px solid #e2e8f0",
              background:     "#ffffff",
              color:          "#64748b",
              cursor:         "pointer",
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
            }}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* ── Stats ──────────────────────────────────────────────────────────── */}
      <StatsBar notifications={notifications} unreadCount={unreadCount} />

      {/* ── Filter tabs ────────────────────────────────────────────────────── */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 px-0.5">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={`notif-filter-${tab.id.toLowerCase()}`}
              onClick={() => applyFilter(tab.id)}
              style={{
                minHeight:    "40px",
                padding:      "8px 16px",
                borderRadius: "9px",
                border:       `1px solid ${isActive ? "#3b82f6" : "#e2e8f0"}`,
                background:   isActive ? "#eff6ff" : "#ffffff",
                color:        isActive ? "#1d4ed8" : "#64748b",
                fontSize:     "13px",
                fontWeight:   isActive ? 700 : 500,
                cursor:       "pointer",
                whiteSpace:   "nowrap",
                transition:   "all 0.15s",
              }}
              className="shrink-0"
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Loading state ───────────────────────────────────────────────────── */}
      {loading && notifications.length === 0 && (
        <div style={{
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          padding:        "60px 0",
          color:          "#94a3b8",
          gap:            "12px",
        }}>
          <Loader2 size={28} style={{ animation: "spin 1s linear infinite" }} />
          <p style={{ fontSize: "13px" }}>Loading notifications…</p>
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────────── */}
      {!loading && notifications.length === 0 && (
        <div style={{
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          justifyContent: "center",
          padding:        "80px 24px",
          background:     "#ffffff",
          borderRadius:   "16px",
          border:         "1px solid #e2e8f0",
          textAlign:      "center",
        }}>
          <div style={{
            width:          "72px",
            height:         "72px",
            borderRadius:   "20px",
            background:     "linear-gradient(135deg, #eff6ff, #dbeafe)",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            marginBottom:   "20px",
            fontSize:       "32px",
          }}>
            🎉
          </div>
          <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#0f172a", marginBottom: "8px" }}>
            You're all caught up!
          </h2>
          <p style={{ fontSize: "13px", color: "#64748b", maxWidth: "280px", lineHeight: 1.5 }}>
            No notifications right now. We'll let you know when something important happens.
          </p>
          {isDev && (
            <button
              onClick={handleSeed}
              disabled={seeding}
              style={{
                marginTop:    "20px",
                padding:      "9px 18px",
                borderRadius: "10px",
                background:   "#3b82f6",
                color:        "#fff",
                fontSize:     "12.5px",
                fontWeight:   600,
                border:       "none",
                cursor:       "pointer",
                display:      "flex",
                alignItems:   "center",
                gap:          "6px",
              }}
            >
              {seeding ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Load Sample Notifications
            </button>
          )}
        </div>
      )}

      {/* ── Notification groups ─────────────────────────────────────────────── */}
      {notifications.length > 0 && (
        <div>
          {groups.map(({ label, items }) => (
            <div key={label} style={{ marginBottom: "8px" }}>
              {/* Date group header */}
              <div style={{
                display:        "flex",
                alignItems:     "center",
                gap:            "10px",
                marginBottom:   "8px",
                marginTop:      label === "Today" ? 0 : "16px",
              }}>
                <span style={{
                  fontSize:      "11px",
                  fontWeight:    700,
                  color:         "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  whiteSpace:    "nowrap",
                }}>
                  {label}
                </span>
                <div style={{ flex: 1, height: "1px", background: "#f1f5f9" }} />
                <span style={{
                  fontSize:   "11px",
                  color:      "#cbd5e1",
                  fontWeight: 500,
                }}>
                  {items.length}
                </span>
              </div>

              {/* Cards */}
              {items.map((n) => (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          ))}

          {/* ── Infinite scroll loader ─────────────────────────────────────── */}
          <div ref={loaderRef} style={{ height: "1px" }} />

          {loading && (
            <div style={{
              display:        "flex",
              justifyContent: "center",
              padding:        "20px",
              color:          "#94a3b8",
            }}>
              <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
            </div>
          )}

          {!hasMore && notifications.length > 0 && (
            <div style={{
              textAlign:   "center",
              padding:     "20px",
              color:       "#cbd5e1",
              fontSize:    "12px",
              fontWeight:  500,
            }}>
              — You've seen all notifications —
            </div>
          )}
        </div>
      )}
    </div>
  );
};
